import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Building2, Phone, Search, Users, X, type LucideIcon } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../i18n/I18nProvider";
import { userCanPlatform, userCanPlatformAny, type StaffUser } from "../../lib/auth";
import { getClients } from "../../lib/clients";
import { autocomplete } from "../../lib/ariregister";
import { formatPhone, normalizeE164 } from "../../lib/phone";
import { useCallContext } from "../telephony/CallProvider";
import { flatNav } from "../nav";

/** Case- and diacritic-insensitive: somebody typing quickly writes "aariregister"
 *  and still means the same page. */
const fold = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

interface Hit {
  key: string;
  group: string;
  label: string;
  /**
   * Everything the query is matched against, already folded: the label and
   * any other name the page goes by (`aliasKeys` in `nav.ts`). Left out, the
   * label alone. A Kliendiotsing tab is listed under its old page's name, and
   * without this the name on the tab strip itself found nothing.
   */
  terms?: string[];
  icon: LucideIcon;
  hint?: string;
  /**
   * Where choosing it goes, or "" for a hit that DOES something instead.
   *
   * Every hit was a destination until the pad arrived. Rather than a second
   * list, a hit may now carry `run`; `choose` prefers it and falls back to
   * navigation, so a future action hit needs no third branch.
   */
  to: string;
  run?: () => void;
}

/**
 * Cmd-K search over the whole CRM.
 *
 * Pages come from the same registry the sidebar renders, so the palette can
 * never offer a destination the rail does not have or one the operator is not
 * allowed to open. Records are fetched live rather than indexed up front: a
 * register of a quarter of a million companies is not something to hold in a
 * dropdown, and the two searches that matter (own clients, the register) are
 * already one request each.
 */
export function CommandPalette({ user, onClose }: { user: StaffUser; onClose: () => void }) {
  const t = useT();
  const navigate = useNavigate();
  // Null outside the provider, which the palette must survive: it is rendered
  // inside `CallProvider` today, and a hook that threw if that ever changed
  // would take the whole search down with it over one optional row.
  const call = useCallContext();
  const [q, setQ] = useState("");
  const [remote, setRemote] = useState<Hit[]>([]);
  const [cursor, setCursor] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const pages = useMemo<Hit[]>(
    () =>
      flatNav()
        .filter((i) => userCanPlatformAny(user, i.perm))
        .map((i) => ({
          key: `page:${i.path}`,
          group: t("palette.pages"),
          label: t(i.labelKey),
          terms: [i.labelKey, ...(i.aliasKeys ?? [])].map((key) => fold(t(key))),
          icon: i.icon,
          to: i.path,
        })),
    [user, t],
  );

  const pageHits = useMemo(() => {
    const f = fold(q.trim());
    if (!f) return pages.slice(0, 8);
    return pages.filter((p) => (p.terms ?? [fold(p.label)]).some((term) => term.includes(f))).slice(0, 8);
  }, [pages, q]);

  // Records are only worth a round trip once the query is a word rather than a
  // keystroke, and the debounce keeps a fast typist to one request.
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setRemote([]);
      return;
    }
    let alive = true;
    const timer = setTimeout(() => {
      const jobs: Promise<Hit[]>[] = [];
      if (userCanPlatform(user, "view:crm")) {
        jobs.push(
          getClients({ q: term, pageSize: 5 })
            .then((r) =>
              r.rows.map((c) => ({
                key: `client:${c.id}`,
                group: t("nav.clients"),
                label: c.name,
                icon: Users as LucideIcon,
                hint: c.email ?? undefined,
                to: `/clients/${c.id}`,
              })),
            )
            .catch(() => []),
        );
      }
      if (userCanPlatform(user, "view:ariregister")) {
        jobs.push(
          autocomplete(term)
            .then((r) =>
              r.items.slice(0, 5).map((e) => ({
                key: `ar:${e.regCode}`,
                group: t("nav.ariregister"),
                label: e.name,
                icon: Building2 as LucideIcon,
                hint: e.regCode,
                to: `/ariregister/${e.regCode}`,
              })),
            )
            .catch(() => []),
        );
      }
      void Promise.all(jobs).then((sets) => {
        if (alive) setRemote(sets.flat());
      });
    }, 220);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [q, user, t]);

  /**
   * A number somebody pasted or typed, offered as a call.
   *
   * FIRST in the list, not last. Anything that normalizes to E.164 was typed as
   * a phone number and matches no page name, so the pages above it would be a
   * row of near-misses standing between the operator and the only hit they
   * meant. It is also the palette's whole answer to "the dial pad cannot be
   * reached from the keyboard": the chord in `useDialShortcut` is the browser's
   * to refuse, and this path is not.
   */
  const dialHit = useMemo<Hit[]>(() => {
    if (!call?.enabled) return [];
    const number = normalizeE164(q);
    if (!number) return [];
    return [
      {
        key: `dial:${number}`,
        group: t("palette.call"),
        label: t("palette.callNumber", { number: formatPhone(number) }),
        icon: Phone as LucideIcon,
        to: "",
        // The pad rather than a straight dial. It is where the line, the
        // recording answer and the do-not-call check live, and a palette that
        // placed the call would be a second way to start one that skipped all
        // three.
        run: () => call.openDialer(number),
      },
    ];
  }, [call, q, t]);

  const hits = useMemo(() => [...dialHit, ...pageHits, ...remote], [dialHit, pageHits, remote]);

  useEffect(() => setCursor(0), [q]);

  useEffect(() => {
    listRef.current?.querySelector(".is-sel")?.scrollIntoView({ block: "nearest" });
  }, [cursor, hits]);

  const choose = (hit: Hit | undefined) => {
    if (!hit) return;
    onClose();
    if (hit.run) {
      hit.run();
      return;
    }
    navigate(hit.to);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, hits.length - 1));
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    }
    if (e.key === "Enter") {
      e.preventDefault();
      choose(hits[cursor]);
    }
  };

  let group: string | null = null;

  return createPortal(
    <div
      className="crm-pal-scrim"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="crm-pal" role="dialog" aria-modal="true" aria-label={t("palette.title")} onKeyDown={onKeyDown}>
        <div className="crm-pal-top">
          <Search className="size-[18px] text-muted-foreground" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("palette.placeholder")}
            autoComplete="off"
            spellCheck={false}
          />
          <button type="button" className="crm-cb" onClick={onClose} aria-label={t("common.close")}>
            <X className="size-4" />
          </button>
        </div>

        <div className="crm-pal-list" ref={listRef}>
          {hits.length === 0 ? (
            <div className="crm-pal-empty">{t("palette.empty")}</div>
          ) : (
            hits.map((hit, i) => {
              const head = hit.group !== group ? hit.group : null;
              group = hit.group;
              const Icon = hit.icon;
              return (
                <div key={hit.key}>
                  {head ? <div className="crm-pal-g">{head}</div> : null}
                  <button
                    type="button"
                    className={cn("crm-pal-i", i === cursor && "is-sel")}
                    onMouseMove={() => setCursor(i)}
                    onClick={() => choose(hit)}
                  >
                    <Icon className="size-4" />
                    <span className="crm-trunc flex-1">{hit.label}</span>
                    {hit.hint ? <span className="crm-pal-hint">{hit.hint}</span> : null}
                  </button>
                </div>
              );
            })
          )}
        </div>

        <div className="crm-pal-foot">
          <span>
            <b className="crm-pal-key">&uarr;&darr;</b>
            {t("palette.move")}
          </span>
          <span>
            <b className="crm-pal-key">&crarr;</b>
            {t("palette.open")}
          </span>
          <span>
            <b className="crm-pal-key">esc</b>
            {t("palette.close")}
          </span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
